import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Checkbox,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  InputAdornment,
  TableSortLabel,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import { Search, List } from '@mui/icons-material';
import useContractNotification from '../CustomHooks/useContractNotification';

function ManageNotificationModal({
  open,
  onClose,
  rowData,
  mode = 'fixed', // 'fixed' or 'dropdown'
  whOptions = [], // Array of WH options for dropdown mode: [{ value: 'WH001', label: 'WH001 - Description' }]
}) {
  const {
    selectedWH,
    setSelectedWH,
    userMatrix,
    notificationOptions,
    toggleChannel,
    toggleNotification,
    sendNotification,
    resetForm,
    newUser,
    setNewUser,
    handleAddUser,
    showCRMList,
    setShowCRMList,
    crmSearch,
    setCrmSearch,
    crmUsers,
    handleAddFromCRM,
    loadingCRM,
    crmListRef,
    existingUsers,
    showDiscardConfirm,
    setShowDiscardConfirm,

    notifyNoRecipient,
    handleToggleNoRecipient,
    savingNoRecipient,
    contractLoaded,
  } = useContractNotification();

  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState(null); // null | 'asc' | 'desc'

  // Set WH number when modal opens (only for fixed mode)
  useEffect(() => {
    if (open && mode === 'fixed' && rowData?.current?.contractUniqueID) {
      setSelectedWH(rowData.current.contractUniqueID);
    } else if (open && mode === 'dropdown') {
      setSelectedWH(null); // Reset for dropdown mode
    }
  }, [open, rowData, mode, setSelectedWH]);

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    let filtered = userMatrix;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        u =>
          u.name?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term) ||
          u.phone?.toLowerCase().includes(term),
      );
    }

    if (sortOrder === 'asc') {
      filtered = [...filtered].sort((a, b) => a.name?.localeCompare(b.name || '') || 0);
    } else if (sortOrder === 'desc') {
      filtered = [...filtered].sort((a, b) => b.name?.localeCompare(a.name || '') || 0);
    }

    return filtered;
  }, [userMatrix, searchTerm, sortOrder]);

  const handleSortClick = () => {
    setSortOrder(prev => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));
  };

  const handleSave = async () => {
    const success = await sendNotification();
    if (success) {
      resetForm();
      onClose();
    }
  };

  const handleCloseModal = () => {
    const newUsers = userMatrix.filter(user => !existingUsers.some(existing => existing.id === user.id));

    if (newUsers.length > 0) {
      setShowDiscardConfirm(true); // open confirmation dialog
      return;
    }

    resetForm();
    setSearchTerm('');
    setSortOrder(null);
    onClose();
  };

  const handleWHChange = event => {
    setSelectedWH(event.target.value);
  };

  // Show empty state if dropdown mode and no WH selected
  const showEmptyState = mode === 'dropdown' && !selectedWH;

  return (
    <>
      <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="xl">
        <DialogTitle
          sx={{
            fontSize: '1.2rem',
            fontWeight: 700,
            borderBottom: '1px solid #e0e0e0',
          }}>
          Manage Contract Notifications
        </DialogTitle>

        <DialogContent style={{ paddingTop: '16px', overflow: 'hidden' }}>
          {/* Header Section */}
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            {/* WH Number Field - Fixed or Dropdown */}
            {mode === 'fixed' ? (
              <TextField label="WH Number" value={selectedWH || ''} size="small" disabled sx={{ width: 300 }} />
            ) : (
              <FormControl size="small" sx={{ width: 300 }}>
                <InputLabel>WH Number</InputLabel>
                <Select value={selectedWH || ''} onChange={handleWHChange} label="WH Number">
                  {whOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Only show controls if WH is selected */}
            {!showEmptyState && (
              <>
                {/* Search Bar */}
                <TextField
                  placeholder="Search by name, email or phone..."
                  size="small"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 350,
                    '& .MuiOutlinedInput-input': { fontSize: '0.9rem' },
                  }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    position: 'relative',
                  }}>
                  <TextField
                    placeholder="Enter email or phone..."
                    size="small"
                    value={newUser}
                    onChange={e => setNewUser(e.target.value)}
                    sx={{ width: 300 }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleAddUser();
                      setSearchTerm(''); // Clear search filter so all users reappear
                    }}
                    disabled={!newUser.trim()}>
                    Add
                  </Button>

                  <IconButton onClick={() => setShowCRMList(p => !p)}>
                    <List />
                  </IconButton>

                  {/* Inline CRM User List Dropdown */}
                  {showCRMList && (
                    <Paper
                      ref={crmListRef}
                      sx={{
                        position: 'absolute',
                        top: '40px',
                        right: 0,
                        width: 450,
                        maxHeight: 300,
                        overflow: 'auto',
                        zIndex: 10,
                        p: 1.5,
                        boxShadow: 4,
                        borderRadius: 2,
                      }}>
                      <TextField
                        placeholder="Search CRM users..."
                        size="small"
                        fullWidth
                        value={crmSearch}
                        onChange={e => setCrmSearch(e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      {loadingCRM ? (
                        <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                          Loading users...
                        </Typography>
                      ) : (
                        <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                          {crmUsers
                            .filter(u => {
                              const term = crmSearch.toLowerCase();
                              return (
                                u.name?.toLowerCase().includes(term) ||
                                u.email?.toLowerCase().includes(term) ||
                                u.phone?.toLowerCase().includes(term)
                              );
                            })
                            .map((u, idx) => (
                              <Box
                                key={`${u.email}-${u.phone}-${idx}`}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  py: 0.8,
                                  borderBottom: '1px solid #eee',
                                }}>
                                <Box>
                                  <Typography fontWeight={500}>{u.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {u.email || '-'} / {u.phone || '-'}
                                  </Typography>
                                </Box>
                                <Button size="small" variant="outlined" onClick={() => handleAddFromCRM(u)}>
                                  Add
                                </Button>
                              </Box>
                            ))}
                        </Box>
                      )}
                    </Paper>
                  )}
                </Box>
              </>
            )}
          </Box>

          {!showEmptyState && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notifyNoRecipient}
                    onChange={e => handleToggleNoRecipient(e.target.checked)}
                    disabled={!contractLoaded || savingNoRecipient}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500}>
                    Send "No Recipient Found" notifications for this contract
                  </Typography>
                }
              />

              {!notifyNoRecipient && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No Recipient notifications will not be sent for this contract ({selectedWH}).
                </Alert>
              )}
            </Box>
          )}

          {/* Empty State or Notification Table */}
          {showEmptyState ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
              }}>
              <Typography variant="h6" color="text.secondary">
                Please select a WH Number to manage notifications
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '65vh', overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  {/* Top row: grouped headers */}
                  <TableRow>
                    {/* USER */}
                    <TableCell
                      rowSpan={2}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: '#f5f5f5',
                        borderRight: '2px solid #ddd', // Border after "User"
                      }}>
                      <TableSortLabel active={!!sortOrder} direction={sortOrder || 'asc'} onClick={handleSortClick}>
                        User
                      </TableSortLabel>
                    </TableCell>

                    {/* CHANNELS (Email + SMS) */}
                    <TableCell
                      align="center"
                      colSpan={2}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: '#f5f5f5',
                        borderRight: '2px solid #ddd', // Border after Channels group
                      }}>
                      Channels
                    </TableCell>

                    {/* NOTIFICATION TYPE */}
                    <TableCell
                      align="center"
                      colSpan={notificationOptions.length}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: '#f5f5f5',
                      }}>
                      Notification Type
                    </TableCell>
                  </TableRow>

                  {/* Second row: sub-columns */}
                  <TableRow>
                    {/* CHANNELS */}
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 500,
                        backgroundColor: '#fafafa',
                      }}>
                      Email
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 500,
                        backgroundColor: '#fafafa',
                        borderRight: '2px solid #ddd', // Border after SMS
                      }}>
                      SMS
                    </TableCell>

                    {/* NOTIFICATION TYPES */}
                    {notificationOptions.map(opt => (
                      <TableCell
                        key={opt.value}
                        align="center"
                        sx={{
                          fontWeight: 500,
                          backgroundColor: '#fafafa',
                        }}>
                        {opt.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map(user => (
                      <TableRow key={user.id} hover>
                        {/* USER */}
                        <TableCell
                          sx={{
                            borderRight: '2px solid #ddd', // Border after User
                          }}>
                          <Typography fontWeight={500}>{user.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email || '-'} / {user.phone || '-'}
                          </Typography>
                        </TableCell>

                        {/* CHANNELS */}
                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            checked={user.channels.email}
                            disabled={!user.email}
                            onChange={() => toggleChannel(user.id, 'email')}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            borderRight: '2px solid #ddd', // Border after SMS
                          }}>
                          <Checkbox
                            size="small"
                            checked={user.channels.sms}
                            disabled={!user.phone}
                            onChange={() => toggleChannel(user.id, 'sms')}
                          />
                        </TableCell>

                        {/* NOTIFICATION TYPES */}
                        {notificationOptions.map(opt => (
                          <TableCell align="center" key={opt.value}>
                            <Checkbox
                              size="small"
                              checked={user.notifications[opt.value]}
                              onChange={() => toggleNotification(user.id, opt.value)}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={notificationOptions.length + 3} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No users match your search.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={showEmptyState}>
            Save Notification
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showDiscardConfirm} onClose={() => setShowDiscardConfirm(false)}>
        <DialogTitle>Discard Changes?</DialogTitle>
        <DialogContent>
          <Typography>
            You’ve added new user{userMatrix.length - existingUsers.length > 1 ? 's' : ''}. They will be <b>deleted</b> if
            you cancel. Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscardConfirm(false)} color="inherit">
            Keep Editing
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              // delete new users and close modal
              setShowDiscardConfirm(false);
              resetForm();
              setSearchTerm('');
              setSortOrder(null);
              onClose();
            }}>
            Delete & Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ManageNotificationModal;
